/* ============================================================================
   INPUT — gom bàn phím + chuột + cảm ứng thành "ý định" mà game hiểu.

   Ở đây KHÔNG có luật chơi. Nó chỉ trả lời hai câu:
     · người chơi đang muốn đi hướng nào  (axis, vector đã chuẩn hoá)
     · người chơi vừa bấm cái gì          (hàng đợi edge)

   Bốn đường vào cùng đổ về một chỗ, nên không có nhánh logic riêng cho cảm ứng:
     bàn phím  → tập phím đang giữ  ─┐
     joystick  → vector analog      ─┼─▶ axis()
     nút ảo    → phím giả           ─┤
     tay cầm   → cần gạt + D-pad    ─┘

   Tay cầm khác ba đường kia ở một điểm: nó không phát sự kiện, phải HỎI VÒNG
   mỗi khung hình (xem `core/gamepad.ts`). Nên `poll()` phải được gọi đúng một
   lần mỗi khung, trước `axis()` và `drain()`.

   Toạ độ trỏ được trả về bằng WORLD PX, không phải pixel màn hình — phần còn
   lại của game không bao giờ phải nghĩ bằng đơn vị màn hình.
============================================================================ */

import { PAD, createGamepad, type PadInfo } from "./gamepad.ts";
import { createInputMode, type InputMode } from "./inputmode.ts";

/* ---------------------------------------------------------------------------
   SƠ ĐỒ NÚT TAY CẦM — bảng DUY NHẤT.

   Vì sao là một bảng chứ không phải một chuỗi `if`: cái người chơi bắt lỗi
   không phải một nút sai, mà là sơ đồ TỰ MÂU THUẪN. Bản trước có ba chỗ chồng
   chéo, và cả ba đều chỉ nhìn ra khi đọc kỹ mười dòng `if` rải rác:

     · RT làm ĐÚNG việc của A ("Dùng (thay cho A)") — một việc, hai nút.
     · LT vừa là CHẠY vừa là phím phụ để vai nhảy năm ô — một nút, hai việc.
     · "Đẩy cần gạt hết cỡ" cũng là chạy, nên chạy có tới HAI cách điều khiển;
       còn hotbar thì có BA (vai, cò + vai, cần phải).

   Trong khi đó việc NGẮM — thứ bàn phím và ngón tay đều làm được — thì tay
   cầm không có đường nào, và cò phải thì mang một việc trùng.

   Bảng ở đây là hợp đồng: MỘT NÚT MỘT VIỆC, MỘT VIỆC MỘT NÚT. Kịch bản sim
   kiểm đúng hai điều đó trên chính bảng này, nên lần sau ai thêm một nút chồng
   lên nút cũ là test đỏ ngay, không phải đọc lại mười dòng `if`.

   Sơ đồ nút hiện trong game cũng dựng TỪ bảng này, nên màn hình không bao giờ
   hứa một nút mà máy không làm — đúng cái lỗi "Chạy" chết âm thầm sáu commit.
--------------------------------------------------------------------------- */

/** Một việc mà tay cầm gọi được. Trùng tên với `Intent` ở đâu được thì trùng. */
export type PadJob =
  | "use"
  | "interact"
  | "back"
  | "inventory"
  | "aimPrev"
  | "aimNext"
  | "hotbarPrev"
  | "hotbarNext"
  | "run"
  | "zoom"
  | "map"
  | "menu";

export interface PadBind {
  nut: number;
  viec: PadJob;
  /** Mô tả in trong sơ đồ nút. */
  mo: string;
  /** Việc GIỮ (không phải bấm một cái) — `poll` bỏ qua, `padState` đọc. */
  giu?: boolean;
  /**
   * Chỉ gán khi trình duyệt xác nhận "standard mapping".
   *
   * Không standard thì chỉ số nút là thứ tự THÔ của phần cứng và mỗi hãng một
   * kiểu — gán bừa thì người chơi bấm nút phía trên lại ra mở balo. Ba nút
   * đứng ngoài hàng rào này: A, B (gần như mọi tay cầm xếp "xác nhận"/"huỷ" ở
   * hai chỗ đầu) và START (chỉ số 9 ở gần như mọi tay cầm) — và nếu đoán sai
   * thì cái giá chỉ là mở nhầm một cái menu đóng lại được, rẻ hơn nhiều so với
   * việc người chơi KHÔNG CÓ đường nào mở menu.
   */
  canStd?: boolean;
}

export const PAD_MAP: readonly PadBind[] = [
  /* HAI nút ngữ cảnh, và chúng trả lời hai câu khác nhau — đúng lời Cường:
     "một nút ngữ cảnh chính là hành động, một nút ngữ cảnh phụ là tra cứu
     thông tin gần đó."

     A làm ĐÚNG MỘT việc, lên đúng thứ mũi tên đỏ đang chỉ. Ngoài tầm thì đi
     tới làm rồi DỪNG.

     Chỗ này từng ghi rằng A "KHÔNG còn nhận cả chuyến", và câu ấy SAI kể từ lúc
     `runAfter` ra đời: mỗi nhát tự nối sang nhát kế, nên bấm một lần để cày một
     ô là cày hết lô rồi nhổ cỏ. Cường gặp lại đúng triệu chứng cũ — "nó nhảy
     tùm lum mà nó cứ làm tự động thôi". Đợt 34 gỡ hẳn đường nối ấy, và kịch bản
     186 nay đối chiếu lời khẳng định này với mã, để nó không sai lặng lẽ lần
     nữa. Muốn làm hàng loạt thì bật "Tự động làm" trong menu. */
  { nut: PAD.A, viec: "use", mo: "Làm — đúng một việc, lên thứ mũi tên đỏ đang chỉ. Ngoài tầm thì đi tới làm rồi dừng." },
  { nut: PAD.B, viec: "interact", mo: "Tra cứu — đọc thông tin của MỤC TIÊU đang nhắm. Không đổi gì cả." },
  /* CHUYỂN MỤC TIÊU nằm ở HAI VAI — Cường: "nếu mà có game pad thì dùng nút
     RB LB để chuyển qua lại các mục tiêu trong phạm vi".

     Trước Đợt 34 chỗ này ghi "KHÔNG có nút chuyển mục tiêu cho tay cầm", với lý
     lẽ: cần phải đã rê thẳng mũi tên tới ô muốn nhắm, thêm nút nữa là phá hợp
     đồng "một nút một việc". Lý lẽ ấy hỏng ở chỗ RÊ và CHỌN không phải một
     việc: rê là chỉ vào một Ô, còn chuyển mục tiêu là đi vòng qua những THỨ
     đáng nhắm quanh mình — cái máng, con bò tới lứa, gốc cây. Rê cần phải tới
     đúng con bò trong lúc nó đang đi là việc khó; bấm vai một cái là xong.

     Hai vai vì nó có CHIỀU: LB lùi, RB tiến, đi vòng tròn quanh mình. */
  { nut: PAD.LB, viec: "aimPrev", mo: "Mục tiêu trước — đi ngược vòng quanh mình.", canStd: true },
  { nut: PAD.RB, viec: "aimNext", mo: "Mục tiêu sau — đi xuôi vòng quanh mình.", canStd: true },
  /* X = QUAY LẠI. Cố ý KHÔNG rào sau `canStd`: từ khi B mang việc tra cứu thì
     đây là nút thoát duy nhất ngoài START, và cắm một tay cầm mà trình duyệt
     không nhận ra sơ đồ chuẩn thì mất hẳn đường lùi. Đoán sai thì cái giá chỉ
     là mở nhầm một cái menu đóng lại được. */
  { nut: PAD.X, viec: "back", mo: "Quay lại — đóng thứ đang mở, không mở gì thì bật menu." },
  /* Y là BALO chứ không phải cửa hàng: cửa hàng là một cái nhà, đi tới nó rồi
     bấm B là xong. Balo thì không có chỗ nào trên bản đồ để đi tới. */
  { nut: PAD.Y, viec: "inventory", mo: "Mở balo.", canStd: true },
  /* HOTBAR dời từ hai VAI xuống hai NÚT CẦN. Hai vai nay mang việc dùng nhiều
     hơn hẳn (chuyển mục tiêu), còn đổi ô hotbar thì thưa hơn — và L3/R3 vừa
     trống ra: "chế độ xây" đã có ô đầu trong lưới menu Tạm dừng từ Đợt 26, còn
     "mở lại bảng nút" cũng nằm sẵn trong menu. */
  { nut: PAD.L3, viec: "hotbarPrev", mo: "Ô hotbar trước.", canStd: true },
  { nut: PAD.R3, viec: "hotbarNext", mo: "Ô hotbar sau.", canStd: true },
  { nut: PAD.LT, viec: "run", mo: "Giữ để chạy.", giu: true, canStd: true },
  { nut: PAD.RT, viec: "zoom", mo: "Đổi mức phóng: gần → vừa → xa.", canStd: true },
  { nut: PAD.BACK, viec: "map", mo: "Bản đồ nhỏ — cần phải rê con trỏ, A để đi tới đó.", canStd: true },
  { nut: PAD.START, viec: "menu", mo: "Menu tạm dừng." },
];

/** Nút mang việc này, hoặc −1. Dùng để sơ đồ nút in ĐÚNG tên nút của máy. */
/**
 * Nút DÙNG có đang được giữ không — hỏi thẳng `PAD_MAP` chứ không liệt kê tay.
 *
 * Chỗ này từng viết `held.has(PAD.A) || held.has(PAD.RT)` trong khi bảng đã
 * giao cò phải cho việc đổi mức phóng: một việc hai nút, đúng cái luật mà chính
 * bảng sinh ra để chặn. Hậu quả là mỗi lần đổi mức phóng lại vung thêm một nhát
 * cuốc. Hỏi bảng thì lệch kiểu đó không xảy ra được nữa.
 */
export function padUseHeld(held: ReadonlySet<number>): boolean {
  for (const m of PAD_MAP) if (m.viec === "use" && held.has(m.nut)) return true;
  return false;
}

export function padButtonFor(job: PadJob): number {
  return PAD_MAP.find((m) => m.viec === job)?.nut ?? -1;
}

export type Intent =
  | { t: "use" }
  | { t: "interact" }
  | { t: "select"; slot: number }
  | { t: "selectDelta"; d: number }
  | { t: "menu" }
  | { t: "shop" }
  | { t: "inventory" }
  | { t: "map" }
  | { t: "debug" }
  /** Bật/tắt chế độ TỰ ĐỘNG LÀM. Cảm ứng còn nút riêng; tay cầm gộp vào nút A. */
  | { t: "auto" }
  /** QUAY LẠI: đóng thứ đang mở, không có gì mở thì bật menu tạm dừng. */
  | { t: "back" }
  /** TẮT popup đang nổi (bảng con vật, con trỏ bản đồ nhỏ, chế độ xây). */
  /** Đổi mức phóng khung nhìn: gần → vừa → xa → gần. */
  | { t: "zoom" }
  /** Bấm/chạm vào thế giới — toạ độ WORLD px. CHỈ CÓ MỘT nghĩa: ĐI tới đó.
   *
   *  Từ Đợt 27 không còn chạm-kép-để-làm. Cường: "con trỏ này chỉ mục đích
   *  định hướng di chuyển". Chạm kép sinh ra vì một con trỏ phải gánh hai
   *  nghĩa — vừa "nơi tôi sẽ đi" vừa "nơi nút DÙNG sẽ tác động"; tách hai
   *  nghĩa ra rồi thì nó thành thừa, và mọi HÀNH ĐỘNG đi qua nút chính. */
  | { t: "pointer"; wx: number; wy: number }
  /** Chuyển sang MỤC TIÊU kế tiếp trong tầm với. `d = -1` là lùi. */
  | { t: "aimNext"; d: 1 | -1 }
  /* ---- KÉO một tuyến (hàng rào, đường nhựa) ----------------------------
     Chỉ phát khi `setDrag(true)`. Cố ý phải bật thủ công chứ không phát mọi
     lúc: đường bấm-để-đi đã được chỉnh rất kỹ để không giật (xem `pointer`),
     và đổ thêm một luồng ý định vào mỗi khung hình ngón tay di chuyển là cách
     nhanh nhất để làm hỏng nó lần nữa. */
  | { t: "drag"; wx: number; wy: number }
  | { t: "dragEnd" }
  /* ---- điều hướng MENU bằng tay cầm ------------------------------------
     Menu là DOM, mà tay cầm không có con trỏ. Ba ý định này để vòng lặp chính
     tự chuyển tiêu điểm giữa các nút trong tấm sheet đang mở. */
  | { t: "navDir"; dx: number; dy: number }
  | { t: "navOk" }
  | { t: "navBack" }
  /** Mở chế độ xây dựng (tay cầm: L3). */
  | { t: "build" }
  /** Mở sơ đồ nút tay cầm (tay cầm: R3). */
  | { t: "padHelp" }
  /** Gạt hướng trên bản đồ khi KHÔNG ở trong menu — dùng để rê con trỏ ô
   *  trong chế độ xây dựng, nơi tay cầm không có chuột để trỏ. */
  | { t: "padAim"; dx: number; dy: number }
  /** Cuộn nội dung menu bằng cần phải. */
  | { t: "navScroll"; dy: number };

export interface Input {
  /** hướng đi mong muốn, độ dài <= 1 */
  axis(): { x: number; y: number };
  /** Bật/tắt luồng ý định `drag`/`dragEnd`. Chỉ bật khi đang vẽ tuyến. */
  setDrag(on: boolean): void;
  /** lấy và XOÁ hàng đợi ý định rời rạc */
  drain(): Intent[];
  /** Vị trí trỏ bằng WORLD px — chỉ trả về khi chuột VỪA cử động gần đây.
   *  Chuột đứng yên một chỗ không được cướp quyền ngắm của bàn phím: người chơi
   *  đi tiếp rồi bấm Space thì phải tác động vào ô TRƯỚC MẶT, chứ không phải ô
   *  mà con trỏ vô tình nằm lên. Trên thiết bị cảm ứng luôn là null. */
  pointer(): { x: number; y: number } | null;
  /** joystick đang được giữ — dùng để ẩn con trỏ ô cho đỡ rối */
  stickActive(): boolean;
  /** Đang muốn CHẠY: giữ Shift, hoặc đẩy joystick gần hết cỡ. Analog nên người
   *  chơi không phải học thêm nút nào — đẩy mạnh là chạy, đúng trực giác. */
  running(): boolean;
  /** Nút DÙNG (Space / nút ảo) đang được GIỮ. Vòng lặp chính dùng nó để tự
   *  chuyển sang ô kế tiếp trong tầm sau khi xong một nhát. */
  useHeld(): boolean;
  /** Đọc tay cầm cho khung hình này. Gọi ĐÚNG MỘT LẦN, trước `axis()`/`drain()`. */
  poll(nowMs: number): void;
  /** Có tay cầm đang cắm không — HUD dùng để đổi gợi ý phím. */
  padConnected(): boolean;
  /** Thiết bị VỪA ĐƯỢC DÙNG. Khác `padConnected()`: cắm tay cầm để đó thì vẫn
   *  là `touch`/`kbm` cho tới khi thật sự bấm vào nó. */
  mode(): InputMode;
  /** Khoá cứng chế độ (từ Cài đặt), `null` = tự nhận. */
  setInputMode(m: InputMode | null): void;
  /** Gọi khi chế độ hiển thị đổi. */
  onModeChange(fn: (m: InputMode) => void): void;
  /** Tay cầm nào đang cắm và nó có gì — để hiện ĐÚNG tên nút, và để biết có
   *  được phép gán các nút phụ hay không. */
  padInfo(): PadInfo;
  /** Rung tay cầm (nếu có). */
  rumble(ms: number, strong?: number): void;
  detach(): void;
}

const RUN_KEYS = new Set(["ShiftLeft", "ShiftRight"]);
const USE_KEYS = new Set(["Space", "Enter"]);

const MOVE_KEYS: Record<string, [number, number]> = {
  KeyW: [0, -1], ArrowUp: [0, -1],
  KeyS: [0, 1], ArrowDown: [0, 1],
  KeyA: [-1, 0], ArrowLeft: [-1, 0],
  KeyD: [1, 0], ArrowRight: [1, 0],
};

export interface JoystickRefs {
  /** vùng nhận chạm (thường là nửa dưới-trái màn hình) */
  zone: HTMLElement;
  /** vòng nền, di chuyển tới chỗ ngón tay đặt xuống */
  base: HTMLElement;
  /** núm, chạy theo ngón tay trong bán kính cho phép */
  knob: HTMLElement;
  /** bán kính tối đa của núm, CSS px */
  radius?: number;
}

export interface InputOptions {
  /** đổi toạ độ màn hình → WORLD px; null nếu bấm ra ngoài khung nhìn */
  toWorld(clientX: number, clientY: number): { x: number; y: number } | null;
  /** true khi đang mở modal — chặn di chuyển nhưng vẫn cho Esc */
  isModalOpen(): boolean;
  joystick?: JoystickRefs;
  /** Máy này CÓ màn cảm ứng không — khả năng, không phải chế độ. */
  coCham?: boolean;
  /** Chế độ lúc chưa ai bấm gì. */
  initialMode?: InputMode;
  /** Khoá cứng từ Cài đặt; `null`/bỏ trống = tự nhận. */
  inputMode?: InputMode | null;
}

export function createInput(target: HTMLElement, opts: InputOptions): Input {
  const pad = createGamepad();
  /* MỘT LÚC MỘT CHẾ ĐỘ. `input.ts` là nơi duy nhất thấy được mọi tín hiệu thô,
     nên nó là nơi duy nhất trả lời được "thiết bị nào vừa dùng". Cố ý KHÔNG
     chặn `axis()`/`useHeld()`/`drain()` theo chế độ: mọi thiết bị vẫn được
     nghe, chế độ chỉ quyết định HIỂN THỊ. Chặn thì đổi thiết bị sẽ cần một cú
     bấm mà cú bấm ấy lại đang bị chặn. */
  const che = createInputMode({
    initial: opts.initialMode ?? "kbm",
    override: opts.inputMode ?? null,
    san: { touch: opts.coCham ?? false, pad: false, kbm: true },
  });
  /** Trạng thái tay cầm của khung hình HIỆN TẠI. */
  let padState = { connected: false, axis: { x: 0, y: 0 }, running: false, useHeld: false };
  const held = new Set<string>();
  const queue: Intent[] = [];
  let ptr: { x: number; y: number } | null = null;
  let ptrAt = 0;
  /** Sau ngần này ms không cử động, con trỏ coi như "bỏ đó", nhường cho bàn phím. */
  const POINTER_STALE_MS = 1500;

  const push = (i: Intent) => {
    // hàng đợi có trần để một phím kẹt không làm phình bộ nhớ vô hạn
    if (queue.length < 32) queue.push(i);
  };

  /* ------------------------------------------------------------ bàn phím */
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.repeat) return;
    che.note("kbm", "press", performance.now());
    const modal = opts.isModalOpen();

    if (e.code === "Escape") {
      push({ t: "menu" });
      e.preventDefault();
      return;
    }
    /* F2 đi qua hàng rào `modal` — vì BẢNG GỠ LỖI tự nó được tính là modal
       (`main.ts` isModalOpen), nên phím mở nó cũng là phím duy nhất đóng được
       nó, mà nếu chặn ở đây thì nó chỉ mở được chứ không tắt được. Escape cũng
       không cứu: `case "menu"` thấy không có menu nào mở nên bật menu Tạm dừng
       đè lên. Còn lại đúng một cái nút ✕ hai chục pixel. */
    if (e.code === "F2") {
      push({ t: "debug" });
      e.preventDefault();
      return;
    }
    if (modal) return;

    if (MOVE_KEYS[e.code] || RUN_KEYS.has(e.code)) {
      held.add(e.code);
      if (MOVE_KEYS[e.code]) e.preventDefault();
      return;
    }
    switch (e.code) {
      case "Space":
      case "Enter":
        held.add(e.code);
        push({ t: "use" });
        e.preventDefault();
        break;
      case "KeyE":
        push({ t: "interact" });
        break;
      /* Q đổi MỤC TIÊU, đối xứng với Tab đổi ô hotbar: giữ Shift là đi ngược.
         Không dùng Tab vì Tab đã là ô hotbar, và một phím hai việc là đúng cái
         luật mà `PAD_MAP` sinh ra để chặn. */
      case "KeyQ":
        push({ t: "aimNext", d: e.shiftKey ? -1 : 1 });
        e.preventDefault();
        break;
      case "KeyB":
        push({ t: "shop" });
        break;
      case "KeyI":
        push({ t: "inventory" });
        break;
      case "KeyM":
        push({ t: "map" });
        break;
      case "KeyF":
        push({ t: "auto" });
        break;
      case "Tab":
        push({ t: "selectDelta", d: e.shiftKey ? -1 : 1 });
        e.preventDefault();
        break;
      default: {
        // 1–9 → ô 1–9, 0 → ô 10 (hotbar cố định 10 ô)
        const m = /^Digit([0-9])$/.exec(e.code);
        if (m) push({ t: "select", slot: m[1] === "0" ? 9 : +m[1]! - 1 });
      }
    }
  };

  const onKeyUp = (e: KeyboardEvent) => held.delete(e.code);

  // Mất focus (alt-tab, mở modal) mà không xoá phím đang giữ thì nhân vật sẽ tự
  // đi mãi một hướng — lỗi kinh điển.
  const onBlur = () => held.clear();

  /* --------------------------------------------------------------- chuột */
  /** Đang bắt kéo không, và ngón/chuột nào đang giữ tuyến. */
  let dragOn = false;
  let dragId: number | null = null;

  const onMove = (e: PointerEvent) => {
    // Đang vẽ tuyến thì NGÓN TAY cũng phải được rê: cả tính năng là "ấn ở đầu
    // đoạn, rê tới cuối". Đây là ngoại lệ duy nhất của luật bên dưới.
    if (dragOn && dragId === e.pointerId) {
      const q = opts.toWorld(e.clientX, e.clientY);
      if (q) push({ t: "drag", wx: q.x, wy: q.y });
      if (e.pointerType === "touch") return;
    }
    // Ngón tay không phải con trỏ: nó không "rê" quanh màn hình để ngắm, nên
    // không cho chạm cập nhật vị trí ngắm — nếu không, ô đang nhắm sẽ dính lại
    // ở chỗ vừa chạm.
    if (e.pointerType === "touch") return;
    const now = performance.now();
    // Rê chuột là tín hiệu TRỤC: lướt ngang qua màn hình không được lật cả HUD.
    che.note("kbm", "axis", now);
    ptr = opts.toWorld(e.clientX, e.clientY);
    ptrAt = now;
  };

  const onUp = (e: PointerEvent) => {
    if (!dragOn || dragId !== e.pointerId) return;
    dragId = null;
    push({ t: "dragEnd" });
  };

  const onDown = (e: PointerEvent) => {
    const p = opts.toWorld(e.clientX, e.clientY);
    if (!p) return;
    const now = performance.now();
    che.note(e.pointerType === "touch" ? "touch" : "kbm", "press", now);
    if (e.pointerType !== "touch") {
      ptr = p;
      ptrAt = now;
    }
    if (dragOn && dragId === null) {
      dragId = e.pointerId;
      /* GIỮ CON TRỎ trong lúc kéo tuyến. Không giữ thì ngón tay rê ra khỏi
         canvas — lên HUD, hay ra mép màn hình, chuyện xảy ra liên tục khi vẽ
         một tuyến sát biên — là `pointerup` bắn ở nơi khác và `dragEnd` không
         bao giờ tới. Phiên kéo kẹt lại: mọi cú chạm sau đó bị hiểu là đang vẽ
         tiếp cái tuyến cũ, và không có cách nào thoát ngoài tải lại trang. */
      try {
        (e.target as Element | null)?.setPointerCapture?.(e.pointerId);
      } catch {
        /* vài trình duyệt từ chối khi con trỏ đã bị bắt ở chỗ khác — không sao */
      }
    }
    push({ t: "pointer", wx: p.x, wy: p.y });
  };

  const onLeave = () => {
    ptr = null;
  };

  const onWheel = (e: WheelEvent) => {
    che.note("kbm", "press", performance.now());
    if (opts.isModalOpen()) return;
    push({ t: "selectDelta", d: e.deltaY > 0 ? 1 : -1 });
    e.preventDefault();
  };

  /* ------------------------------------------------------------ joystick */
  let stick = { x: 0, y: 0 };
  let stickId: number | null = null;
  const js = opts.joystick;
  /* Bán kính LẤY TỪ CHÍNH VÒNG NỀN đang hiện, không phải một hằng số chép tay.
     Trước đây JS dùng 46 trong khi CSS vẽ vòng 112px (bán kính 56): núm chạy
     hết tầm khi mới tới 82% vòng, nên người chơi tưởng còn đẩy được nữa mà
     thực ra đã max. Và ở màn hình thấp CSS thu vòng còn 92px (bán kính 46)
     trong khi JS vẫn 46 — hai con số chỉ tình cờ gặp nhau ở đúng một khổ màn.
     Đo lúc ĐẶT NGÓN XUỐNG thì nó luôn khớp với thứ mắt đang thấy. */
  const banKinh = (): number => {
    const w = js?.base?.offsetWidth ?? 0;
    return w > 0 ? w / 2 : (js?.radius ?? 46);
  };
  let radius = banKinh();
  /** Dưới ngưỡng này coi như không đẩy — ngón tay đặt hờ không làm nhân vật trôi. */
  const STICK_DEAD = 0.24;
  /** Đẩy quá ngưỡng này thì chuyển sang chạy. */
  const STICK_RUN = 0.86;

  const stickReset = () => {
    stickId = null;
    stick = { x: 0, y: 0 };
    if (js) {
      js.base.style.opacity = "0";
      js.knob.style.transform = "translate(-50%,-50%)";
    }
  };

  const stickDown = (e: PointerEvent) => {
    che.note("touch", "press", performance.now());
    if (stickId !== null || opts.isModalOpen()) return;
    stickId = e.pointerId;
    js!.zone.setPointerCapture(e.pointerId);
    // Joystick ĐỘNG: mọc ra ngay chỗ ngón tay đặt xuống, thay vì bắt người chơi
    // mò tới một vòng tròn cố định. Trên màn nhỏ đây là khác biệt lớn.
    const r = js!.zone.getBoundingClientRect();
    js!.base.style.left = `${e.clientX - r.left}px`;
    js!.base.style.top = `${e.clientY - r.top}px`;
    js!.base.style.opacity = "1";
    // Đo lại ngay lúc này: cỡ vòng nền đổi theo @media, mà lúc dựng input thì
    // chưa chắc đã biết khổ màn cuối cùng.
    radius = banKinh();
    js!.base.dataset["ox"] = String(e.clientX);
    js!.base.dataset["oy"] = String(e.clientY);
    e.preventDefault();
  };

  const stickMove = (e: PointerEvent) => {
    if (stickId !== e.pointerId) return;
    const ox = Number(js!.base.dataset["ox"] ?? 0);
    const oy = Number(js!.base.dataset["oy"] ?? 0);
    let dx = e.clientX - ox;
    let dy = e.clientY - oy;
    const len = Math.hypot(dx, dy);
    if (len > radius) {
      dx = (dx / len) * radius;
      dy = (dy / len) * radius;
    }
    js!.knob.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;

    const nx = dx / radius;
    const ny = dy / radius;
    const mag = Math.hypot(nx, ny);
    if (mag < STICK_DEAD) {
      stick = { x: 0, y: 0 };
    } else {
      // Trải lại phần trên vùng chết ra full 0..1 để không bị "nhảy cóc" tốc độ
      // ngay khi vượt ngưỡng.
      const k = Math.min(1, (mag - STICK_DEAD) / (1 - STICK_DEAD)) / mag;
      stick = { x: nx * k, y: ny * k };
    }
    e.preventDefault();
  };

  const stickUp = (e: PointerEvent) => {
    if (stickId !== e.pointerId) return;
    stickReset();
    e.preventDefault();
  };

  /* --------------------------------------------------------------- gắn */
  window.addEventListener("keydown", onKeyDown);
  window.addEventListener("keyup", onKeyUp);
  window.addEventListener("blur", onBlur);
  target.addEventListener("pointermove", onMove);
  target.addEventListener("pointerdown", onDown);
  target.addEventListener("pointerleave", onLeave);
  target.addEventListener("pointerup", onUp);
  target.addEventListener("pointercancel", onUp);
  target.addEventListener("wheel", onWheel, { passive: false });

  if (js) {
    js.zone.addEventListener("pointerdown", stickDown);
    js.zone.addEventListener("pointermove", stickMove);
    js.zone.addEventListener("pointerup", stickUp);
    js.zone.addEventListener("pointercancel", stickUp);
    stickReset();
  }

  return {
    poll(nowMs) {
      const st = pad.poll(nowMs);
      padState = {
        connected: st.connected,
        axis: st.axis,
        running: st.running,
        // Giữ nút DÙNG, nên "giữ để làm tiếp ô kế bên" chạy y hệt như giữ Space.
        useHeld: padUseHeld(st.held),
      };
      /* Tay cầm CÓ THẬT hay không quyết định nó có được phép thắng chế độ.
         Cái bóng `connected: true, buttons: []` sau khi rút đã bị `gamepad.ts`
         lọc, nên tới đây `connected` là thật. */
      che.setSan("pad", st.connected);
      if (!st.connected) return;

      /* Chế độ đổi theo HOẠT ĐỘNG: bấm nút là đổi ngay, đẩy cần thì phải giữ.
         Cắm tay cầm rồi để đó không đổi gì cả — đúng thứ Cường muốn. */
      if (st.pressed.size > 0) che.note("pad", "press", nowMs);
      else if (st.axis.x !== 0 || st.axis.y !== 0 || st.navDir || st.aimDir)
        che.note("pad", "axis", nowMs);

      const modal = opts.isModalOpen();

      /* Trong MENU thì tay cầm điều khiển TIÊU ĐIỂM, không điều khiển nhân vật.
         Không tách hai chế độ thì bấm A để chọn "Chơi mới" cũng đồng thời vung
         cuốc xuống ô dưới chân. */
      if (modal) {
        if (st.navDir) push({ t: "navDir", dx: st.navDir.x, dy: st.navDir.y });
        if (st.pressed.has(PAD.A)) push({ t: "navOk" });
        if (st.pressed.has(PAD.B) || st.pressed.has(PAD.START)) push({ t: "navBack" });
        /* X cũng ĐÓNG. Trong lớp phủ thì `PAD_MAP` không được đọc tới (nhánh
           này thoát sớm), nên nếu không có dòng này thì "X tắt popup" đúng ở
           ngoài ruộng mà chết ngay khi có popup thật để tắt — tức là chết ở
           đúng chỗ nó sinh ra để làm. Chỉ khi sơ đồ chuẩn: tay cầm lạ thì chỉ
           số 2 chẳng biết là nút nào. */
        if (pad.info().standard && st.pressed.has(PAD.X)) push({ t: "navBack" });
        // Cần phải cuộn nội dung menu — cửa hàng có bốn mươi thẻ hạt.
        if (st.aimDir?.y) push({ t: "navScroll", dy: st.aimDir.y });
        // Vai đổi TAB trong menu — main.ts hiểu `selectDelta` theo ngữ cảnh.
        if (pad.info().standard) {
          if (st.pressed.has(PAD.LB)) push({ t: "selectDelta", d: -1 });
          if (st.pressed.has(PAD.RB)) push({ t: "selectDelta", d: 1 });
        }
        return;
      }

      /* Sơ đồ nút đọc thẳng từ `PAD_MAP` — một bảng, một nguồn. Xem chú thích
         của bảng ở đầu file để biết vì sao nó phải là bảng chứ không phải một
         chuỗi `if`. */
      const std = pad.info().standard;
      for (const m of PAD_MAP) {
        if (m.giu) continue; // việc GIỮ (chạy) đọc ở `padState`, không phải sườn lên
        if (m.canStd && !std) continue;
        if (!st.pressed.has(m.nut)) continue;
        switch (m.viec) {
          case "use": push({ t: "use" }); break;
          case "interact": push({ t: "interact" }); break;
          case "back": push({ t: "back" }); break;
          case "inventory": push({ t: "inventory" }); break;
          case "aimPrev": push({ t: "aimNext", d: -1 }); break;
          case "aimNext": push({ t: "aimNext", d: 1 }); break;
          case "hotbarPrev": push({ t: "selectDelta", d: -1 }); break;
          case "hotbarNext": push({ t: "selectDelta", d: 1 }); break;
          case "zoom": push({ t: "zoom" }); break;
          case "map": push({ t: "map" }); break;
          case "menu": push({ t: "menu" }); break;
        }
      }

      /* CẦN PHẢI = RÊ CON TRỎ NGẮM. Cần trái đi, cần phải ngắm — hai ngón cái
         hai việc, đúng như mọi tay cầm khác trên đời. Trước đây cần phải đổi ô
         hotbar, tức là hotbar có tới BA cách điều khiển (vai, cò+vai, cần
         phải) trong khi việc ngắm — thứ mà bàn phím và ngón tay đều làm được —
         thì tay cầm không có cách nào. `main.ts` hiểu ý định này theo ngữ
         cảnh: bản đồ nhỏ thì rê con trỏ bản đồ, chế độ xây thì rê ô xây, còn
         lại thì rê ô ngắm quanh nhân vật. */
      const re = st.aimDir ?? st.navDir;
      if (re) push({ t: "padAim", dx: re.x, dy: re.y });
    },
    padConnected: () => padState.connected,
    mode: () => che.mode(),
    setInputMode: (m) => che.setOverride(m),
    onModeChange: (fn) => che.onChange(fn),
    padInfo: () => pad.info(),
    rumble: (ms, strong) => pad.rumble(ms, strong),

    setDrag(on) {
      dragOn = on;
      if (!on) dragId = null;
    },
    axis() {
      let x = 0;
      let y = 0;
      for (const code of held) {
        const v = MOVE_KEYS[code];
        if (!v) continue;
        x += v[0];
        y += v[1];
      }
      if (x === 0 && y === 0 && (stick.x !== 0 || stick.y !== 0)) {
        x = stick.x;
        y = stick.y;
      }
      // Tay cầm là đường vào CUỐI: bàn phím và joystick cảm ứng thắng, để cắm
      // tay cầm rồi vẫn gõ WASD được mà không phải rút ra.
      if (x === 0 && y === 0 && (padState.axis.x !== 0 || padState.axis.y !== 0)) {
        x = padState.axis.x;
        y = padState.axis.y;
      }
      const len = Math.hypot(x, y);
      // chuẩn hoá để đi chéo không nhanh hơn đi thẳng
      return len > 1 ? { x: x / len, y: y / len } : { x, y };
    },
    drain() {
      if (queue.length === 0) return [];
      return queue.splice(0, queue.length);
    },
    pointer: () => (ptr && performance.now() - ptrAt < POINTER_STALE_MS ? ptr : null),
    stickActive: () => stickId !== null,
    /* Ba đường vào đều nói được câu "đang chạy", và cả ba phải được hỏi.
       Dòng này từng quên mất tay cầm trong khi `useHeld` ngay dưới thì nhớ —
       một sự bất đối xứng không ai nhìn ra khi đọc, vì nó không crash, không
       cảnh báo, chỉ làm người cầm tay cầm đi bộ suốt ván mà không hiểu vì sao.
       Tệ hơn: sơ đồ nút vẫn quảng cáo cả LT lẫn "đẩy mạnh là chạy". */
    running: () =>
      padState.running ||
      held.has("ShiftLeft") ||
      held.has("ShiftRight") ||
      Math.hypot(stick.x, stick.y) >= STICK_RUN,
    useHeld: () => padState.useHeld || [...USE_KEYS].some((k) => held.has(k)),
    detach() {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onBlur);
      target.removeEventListener("pointermove", onMove);
      target.removeEventListener("pointerdown", onDown);
      target.removeEventListener("pointerup", onUp);
      target.removeEventListener("pointercancel", onUp);
      target.removeEventListener("pointerleave", onLeave);
      target.removeEventListener("wheel", onWheel);
      if (js) {
        js.zone.removeEventListener("pointerdown", stickDown);
        js.zone.removeEventListener("pointermove", stickMove);
        js.zone.removeEventListener("pointerup", stickUp);
        js.zone.removeEventListener("pointercancel", stickUp);
      }
      held.clear();
    },
  };
}

/** Nút bấm ảo: bơm phím giả vào cùng đường xử lý ở trên, nên không có nhánh
 *  logic riêng cho cảm ứng. */
export function bindTouchButton(el: HTMLElement, code: string) {
  const fire = (type: "keydown" | "keyup") => {
    window.dispatchEvent(new KeyboardEvent(type, { code, bubbles: true }));
  };
  el.addEventListener("pointerdown", (e) => {
    e.preventDefault();
    e.stopPropagation();
    el.setPointerCapture(e.pointerId);
    el.classList.add("down");
    fire("keydown");
  });
  const stop = (e: PointerEvent) => {
    e.preventDefault();
    el.classList.remove("down");
    fire("keyup");
  };
  el.addEventListener("pointerup", stop);
  el.addEventListener("pointercancel", stop);
}
